import { cn } from '@brew/ui/cn'
import { MailOpen, Repeat2 } from 'lucide-react'
import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'
import type { ReactNode } from 'react'
import { formatRemixCount } from '@/lib/templates/format-remix-count'

/**
 * Template metrics and credits, in the reader's language (`templates`
 * catalog). Rendered on the server (the template page) and on the client
 * (gallery cards, the home page's remix visual), so every component here
 * uses `useTranslations` and `useLocale`, which work in both.
 */

export type TemplateDesignedBy = {
  name: string
  website: string
  logoUrl: string
}

function designerName(chunks: ReactNode) {
  return <span className="font-medium text-foreground">{chunks}</span>
}

export function TemplateOpenCount({
  openedCount,
  className,
}: {
  openedCount: number
  className?: string
}) {
  const t = useTranslations('templates')
  const locale = useLocale()
  const label = t('metadata.opens', { count: openedCount })

  return (
    <span
      role="group"
      aria-label={label}
      title={label}
      className={cn(
        'flex shrink-0 items-center gap-1 text-muted-foreground text-xs tabular-nums',
        className
      )}
    >
      <MailOpen aria-hidden className="size-3.5" />
      {formatRemixCount(openedCount, locale)}
    </span>
  )
}

export function TemplateRemixCount({
  remixCount,
  className,
}: {
  remixCount?: number
  className?: string
}) {
  const t = useTranslations('templates')
  const locale = useLocale()
  if (!remixCount || remixCount < 1) {
    return null
  }

  const label = t('metadata.remixed', { count: remixCount })

  return (
    <span
      role="group"
      aria-label={label}
      title={label}
      className={cn(
        'flex shrink-0 items-center gap-1 text-muted-foreground text-xs tabular-nums',
        className
      )}
    >
      <Repeat2 aria-hidden className="size-3.5" />
      {formatRemixCount(remixCount, locale)}
    </span>
  )
}

export function TemplateDetailMetadata({
  remixCount,
  designedBy,
  className,
}: {
  remixCount?: number
  designedBy?: TemplateDesignedBy
  className?: string
}) {
  const t = useTranslations('templates')
  const hasRemixes = Boolean(remixCount && remixCount > 0)
  if (!(hasRemixes || designedBy)) {
    return null
  }

  return (
    <div
      className={cn(
        'flex w-full flex-wrap items-center justify-end gap-x-4 gap-y-2',
        className
      )}
    >
      <TemplateRemixCount remixCount={remixCount} />
      {designedBy ? (
        <a
          href={designedBy.website}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('metadata.designedByLabel', { name: designedBy.name })}
          className="flex min-w-0 items-center gap-2 rounded-lg text-muted-foreground text-xs transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Image
            src={designedBy.logoUrl}
            alt={t('logoAlt', { name: designedBy.name })}
            width={24}
            height={24}
            unoptimized
            className="size-6 shrink-0 rounded-full bg-white object-contain"
          />
          <span className="truncate">
            {t.rich('metadata.designedBy', {
              name: designedBy.name,
              designer: designerName,
            })}
          </span>
        </a>
      ) : null}
    </div>
  )
}
